'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { fetcher } from '@utils/data-fetching';
import {
  buildLandingPath,
  extractUtmParams,
  hasAnalyticsConsent,
  UtmLastTouch,
} from '@utils/analytics/utm';

export const useUtmAttribution = () => {
  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();
  const { locale } = useExtendedRouter();
  const { isAuth } = useAuthClient();
  const [consentGranted, setConsentGranted] = useState(hasAnalyticsConsent());
  const lastSentRef = useRef<string | null>(null);

  useEffect(() => {
    const onConsentUpdate = () => setConsentGranted(hasAnalyticsConsent());
    window.addEventListener('analytics-consent-updated', onConsentUpdate as EventListener);
    return () => window.removeEventListener('analytics-consent-updated', onConsentUpdate as EventListener);
  }, []);

  const sendIfNeeded = async (payload: UtmLastTouch) => {
    const payloadKey = JSON.stringify(payload);
    if (lastSentRef.current === payloadKey) return;
    lastSentRef.current = payloadKey;
    try {
      await fetcher('/v1/user/utm-tracking', locale || 'ro', {
        method: 'POST',
        payload,
        stringifyPayload: true,
      });
    } catch {
      // Best-effort only.
    }
  };

  useEffect(() => {
    if (!consentGranted) return;

    const searchParamsString = searchParams?.toString() ?? '';
    const fallbackSearch = typeof window !== 'undefined'
      ? window.location.search.replace(/^\?/, '')
      : '';
    const effectiveSearch = searchParamsString || fallbackSearch;
    const utmParams = extractUtmParams(new URLSearchParams(effectiveSearch));
    if (!utmParams) return;

    const lastTouch: UtmLastTouch = {
      ...utmParams,
      timestamp: new Date().toISOString(),
      landing_path: buildLandingPath(pathname, effectiveSearch),
    };

    if (isAuth) {
      void sendIfNeeded(lastTouch);
    }
  }, [pathname, searchParams, isAuth, consentGranted]);
};
